import { SEARCH_HIGHLIGHT_QUERY_PARAM } from '@/lib/academySearch/academySearch.const'
import type { AcademySearchApiPayload } from '@/lib/academySearch/academySearch.interface'

import {
  SEARCH_MIN_QUERY_LENGTH,
  SEARCH_MODAL_FILTER_CHIP_IDS,
  SEARCH_MODAL_PREVIEW_PER_CATEGORY_LIMIT,
  searchModalFilterChips,
} from './search-modal.const'
import type { SearchModalFilterChipId, SearchModalTabId } from './search-modal.type'

const DEFAULT_ERROR_MESSAGE = 'Search failed.'

const normalizeSubtitle = (value: string): string => value.replace(/\s+/g, ' ').replace(/…/g, '...').trim()

const withTrailingDots = (value: string): string => {
  if (value.endsWith('...')) {
    return value
  }

  return `${value.replace(/[.!?]+$/, '')}...`
}

export function buildSearchModalSubtitle(description: string, snippet: string, query: string): string | undefined {
  const hasQuery = query.trim().length > 0
  const preferred = normalizeSubtitle(hasQuery ? snippet : description)
  const fallback = normalizeSubtitle(hasQuery ? description : snippet)
  const subtitle = preferred || fallback

  if (!subtitle) {
    return undefined
  }

  return hasQuery ? withTrailingDots(subtitle) : subtitle
}

export function buildSearchResultHref(pathHref: string, queryTrimmed: string, headingId?: string): string {
  const [pathAndQuery] = pathHref.split('#')
  const highlight = queryTrimmed.trim()
  let href = pathAndQuery ?? pathHref

  if (highlight) {
    const separator = href.includes('?') ? '&' : '?'
    href = `${href}${separator}${SEARCH_HIGHLIGHT_QUERY_PARAM}=${encodeURIComponent(highlight)}`
  }

  if (headingId) {
    return `${href}#${headingId}`
  }

  return href
}

export function buildSearchUrl(query: string): string {
  return `/api/docs/search?q=${encodeURIComponent(query)}`
}

export function limitSearchPayloadCategories(payload: AcademySearchApiPayload, limit: number): AcademySearchApiPayload {
  return {
    documentation: payload.documentation.slice(0, limit),
    glossary: payload.glossary.slice(0, limit),
    quickStart: payload.quickStart.slice(0, limit),
  }
}

export function isAbortError(err: unknown): boolean {
  return (err as { name?: string }).name === 'AbortError'
}

export async function fetchSearch(
  url: string,
  init: RequestInit | undefined,
  trimmedQuery: string
): Promise<AcademySearchApiPayload> {
  try {
    const response = await fetch(url, init)

    if (!response.ok) {
      throw new Error(`Search failed (${String(response.status)})`)
    }

    const data = (await response.json()) as AcademySearchApiPayload

    if (trimmedQuery.length === 0) {
      return limitSearchPayloadCategories(data, SEARCH_MODAL_PREVIEW_PER_CATEGORY_LIMIT)
    }

    return data
  } catch (err: unknown) {
    if (isAbortError(err)) {
      throw err
    }
    throw err instanceof Error ? err : new Error(DEFAULT_ERROR_MESSAGE)
  }
}

export function getSearchErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE
}

export function getSearchModalFilterChipLabel(id: SearchModalFilterChipId): string {
  return searchModalFilterChips.find((chip) => chip.id === id)?.label ?? id
}

export function isEnabledFilterChipId(id: string): id is SearchModalFilterChipId {
  switch (id) {
    case SEARCH_MODAL_FILTER_CHIP_IDS.all:
    case SEARCH_MODAL_FILTER_CHIP_IDS.quickStart:
    case SEARCH_MODAL_FILTER_CHIP_IDS.documents:
    case SEARCH_MODAL_FILTER_CHIP_IDS.glossary:
      return true
    default:
      return false
  }
}

export function isSearchQueryTooShort(queryTrimmed: string): boolean {
  return queryTrimmed.length > 0 && queryTrimmed.length < SEARCH_MIN_QUERY_LENGTH
}

export function isSearchTabId(id: string): id is SearchModalTabId {
  return id === 'academy'
}
